<template>
    <section class="amTween" @click="onExpand(tweenNew.id)">
        <div class="amTween__content">
            <button
                class="amTween__drag"
                type="button"
                data-drag-handle
                @click.stop
            >
                <font-awesome-icon :icon="['far', 'arrows-alt']" />
            </button>

            <div class="amTween__summary" :contenteditable="true" v-html="getLabel(tweenNew)" />

            <button class="amTween__arrow" type="button">
                <font-awesome-icon :icon="['far', getCollapseIcon(tweenNew.id)]" />
            </button>
        </div>

        <collapse :id="uniqid()" :visible="isExpanded(tween.id)" class="amTween__collapse">
            <ul class="amTween__vars" @click.stop>
                <li v-for="tweenVar in tweenNew.vars" :key="tweenVar.prop" class="amTween__var">
                    <animation-effect
                        :tween-var="tweenVar"
                        :selected-props="getSelectedProps()"
                        @input="onVarInput"
                        @delete="onVarDelete"
                    />
                </li>

                <li class="amTween__var amTween__var--add">
                    <button class="amTween__var-add" type="button" @click="onVarAdd(tweenNew)">
                        <font-awesome-icon :icon="['far', 'plus']" />
                    </button>
                </li>
            </ul>

            <div class="amTween__settings" @click.stop>
                <div class="amTween__setting">
                    <span class="amTween__setting-label">DURATION</span>
                    <input class="amTween__setting-input" :value="tweenNew.duration" @input="onDurationInput">
                    <span class="amTween__setting-postfix">{{ getDurationText(tweenNew.duration) }}</span>
                </div>

                <div class="amTween__setting">
                    <span class="amTween__setting-label">DELAY</span>
                    <input class="amTween__setting-input" :value="tweenNew.delay" @input="onDelayInput">
                    <span class="amTween__setting-postfix">{{ getDurationText(tweenNew.delay) }}</span>
                </div>

                <div class="amTween__setting">
                    <span class="amTween__setting-label">EASE</span>

                    <dropdown
                        class="amTween__setting-dropdown"
                        :options="easeOptions"
                        :value="tweenNew.ease"
                        @input="onEaseInput"
                    />

                    <VDropdown class="amTween__setting-icon">
                        <slot>
                            <font-awesome-icon :icon="['fas', 'question-circle']" />
                        </slot>

                        <template #popper>
                            <div class="amTween__setting-help">
                                Eases affect the rate of change for your animation. You can learn more about each one by <a href="https://greensock.com/docs/v3/Eases" target="_blank">clicking here</a>.
                            </div>
                        </template>
                    </VDropdown>
                </div>
            </div>
        </collapse>
    </section>
</template>

<script>
import uniqid from 'uniqid';

import AnimationEffect from '@/components/settings/component/animations/animation-effect';
import { TweenVar } from '@/data/data-models';
import tweenVars, { tweenVarProps } from '@/data/animations/tween-vars';
import mapFilters from '@/utils/map-filters';

export default {
    name: 'animation-step',
    components: { AnimationEffect },

    props: {
        tween: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            expandedId: '',
            tweenNew: {},
            tweenVars,
            uniqid,

            easeOptions: [
                { text: 'None', value: '' },
                { text: 'Power 1', value: 'power1.out' },
                { text: 'Power 2', value: 'power2.out' },
                { text: 'Power 3', value: 'power3.out' },
                { text: 'Power 4', value: 'power4.out' },
                { text: 'Back', value: 'back.out(4)' },
                { text: 'Elastic', value: 'elastic.out(1, 0.3)' },
                { text: 'Bounce', value: 'bounce.out' },
                { text: 'Slow', value: 'slow(0.7, 0.7, false' },
                { text: 'Steps', value: '12' },
                { text: 'Circ', value: 'circ.out' },
                { text: 'Expo', value: 'expo.out' },
                { text: 'Sine', value: 'sine.out' },
            ],
        };
    },

    created() {
        this.tweenNew = this.tween;
    },

    methods: {
        ...mapFilters(['titleCase']),

        onExpand(tweenId) {
            this.expandedId = tweenId === this.expandedId ? '' : tweenId;
        },

        onDurationInput(event) {
            const duration = event.target.value;
            const isNumber = !Number.isNaN(Number(duration));
            const numDecimals = duration.match(/\./g) ? duration.match(/\./g).length : 0;

            if (numDecimals === 2) {
                const endIndex = duration.endsWith('..') ? -2 : -1;

                this.tweenNew.duration = duration.slice(0, endIndex);
                this.onUpdate(this.tweenNew);
                return;
            }

            if (!duration || !isNumber || duration.endsWith('.')) return;

            this.tweenNew.duration = Number(duration);
            this.onUpdate(this.tweenNew);
        },

        onDelayInput(event) {
            this.tweenNew.delay = event.target.value;
            this.onUpdate(this.tweenNew);
        },

        onEaseInput(value) {
            this.tweenNew.ease = value;
            this.onUpdate(this.tweenNew);
        },

        onVarAdd(tween) {
            const previousVar = this.tween.vars[this.tween.vars.length - 1];

            if (previousVar && !previousVar.prop) return;

            const tweenVar = new TweenVar({ id: uniqid() });

            tween.vars.push(tweenVar);
        },

        onVarInput(tweenVarNew) {
            const index = this.getIndexOf(tweenVarNew.id);

            this.tweenNew.vars[index] = tweenVarNew;
            this.onUpdate();
        },

        onVarDelete(tweenVarId) {
            const index = this.getIndexOf(tweenVarId);

            this.tweenNew.vars.splice(index, 1);
            this.onUpdate();
        },

        onUpdate() {
            this.$forceUpdate();
            this.$emit('input', this.tweenNew);
        },

        getCollapseIcon(tweenId) {
            return this.isExpanded(tweenId) ? 'chevron-up' : 'chevron-down';
        },

        getDurationText(count) {
            return count === 1 ? 'second' : 'seconds';
        },

        getIndexOf(tweenVarId) {
            return this.tweenNew.vars.findIndex(v => v.id === tweenVarId);
        },

        getLabel(tween) {
            const secondsText = this.getDurationText(tween.duration);
            let effects = tween.vars.filter(tweenVar => !!tweenVar.prop);

            effects = effects.map((tweenVar, i) => {
                let text = `<strong>${ this.titleCase(tweenVar.label) }</strong>`;
                let verb = '';

                switch (tweenVar.prop) {
                    case tweenVarProps.UP:
                    case tweenVarProps.DOWN:
                    case tweenVarProps.LEFT:
                    case tweenVarProps.RIGHT:
                        verb = 'Move';
                        break;

                    default:
                        verb = 'Change';
                }

                if (i !== 0) verb = verb.toLowerCase();

                text = `${ verb } ${ text }`;

                // if not one of the last two items, add a comma
                if (i < effects.length - 2) {
                    return `${ text },`;
                }

                return text;
            });

            // converting to a Set strips out any duplicate values
            effects = Array.from(new Set(effects));

            if (effects.length > 1) {
                effects.splice(effects.length - 1, 0, 'and');
            }

            return effects.length
                ? `${ effects.join(' ') } for <strong>${ tween.duration } ${ secondsText }</strong>.`
                : 'Do <strong>Nothing</strong>';
        },

        getSelectedProps() {
            return this.tweenNew.vars.map(v => v.prop);
        },

        isExpanded(tweenId) {
            return tweenId === this.expandedId;
        },
    },
};
</script>

<style lang="scss">
.amTween {
    &__content {
        position: relative;
        display: flex;
        align-items: center;
        padding: 0.5em 1em;

        &:hover {
            cursor: pointer;
        }
    }

    &__drag {
        margin-right: 1em;
        font-size: 0.875em;

        &:hover {
            cursor: grab;
        }

        &:active {
            cursor: grabbing;
        }
    }

    &__summary {
        font-size: 0.875rem;

        strong {
            color: var(--form-controlColor);
        }
    }

    &__arrow {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: auto;

        &:hover {
            cursor: pointer;
        }
    }

    &__collapse {
        display: flex;
        background-color: var(--app-backgroundColor);
        border: 10px solid var(--app-hoverBackgroundColor);
        border-top: none;
    }

    &__vars {
        display: flex;
        flex-grow: 1;
        flex-direction: column;
    }

    &__var {
        position: relative;
        display: flex;
        padding: 0.75em 1em;

        &:not(:first-of-type) {
            border-top: 1px solid var(--app-borderColor);
        }

        &--add {
            flex-grow: 1;
            align-items: normal;
            padding: 0;
            background-color: var(--app-hoverBackgroundColor);
        }
    }

    &__var-add {
        padding: 0.75em 1.25em;
        width: 100%;
        background-color: var(--app-backgroundColor);
    }

    &__settings {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        width: 18em;
        padding: 1em;
        border-left: 10px solid var(--app-hoverBackgroundColor);
    }

    &__setting {
        display: flex;

        &:not(:first-of-type) {
            margin-top: 1em;
        }
    }

    &__setting-label {
        display: inline-flex;
        align-items: center;
        padding: 0.5em 1em;
        font-weight: 700;
        font-size: 0.875rem;
        color: var(--form-labelColor);
        background-color: var(--app-hoverBackgroundColor);
        border: 1px solid var(--app-borderColor);
        border-right: none;
    }

    &__setting-input {
        @extend %input-display;
        padding: 0.25em 1em;
        width: 4rem;
        border-color: var(--app-borderColor);
        border-left: none;
    }

    &__setting-dropdown {
        padding: 0.5em 3em 0.5em 1em !important;
        border-color: var(--app-borderColor) !important;
        border-left: none !important;
        background-color: transparent !important;
        font-size: 0.875rem !important;
    }

    &__setting-postfix {
        display: inline-flex;
        align-items: center;
        color: var(--form-labelColor);
        font-size: 0.875rem;
        margin-left: 0.75em;
        font-weight: 600;
    }

    &__setting-icon {
        align-self: center;
        margin-left: 1em;
        color: var(--form-labelColor);

        &:hover {
            cursor: pointer;
            color: var(--app-color);
        }
    }

    &__setting-help {
        font-size: 0.875rem;

        a,
        a:visited {
            color: var(--form-buttonBackgroundColor);
        }

        a:hover {
            color: var(--form-buttonHoverBackgroundColor);
        }
    }
}
</style>
